import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Text } from "@semcore/typography";
import Input from "@semcore/ui/input";
import { Box, Flex } from "@semcore/ui/flex-box";
import { getDomainParts, getUrlType } from "../helper";
import { useSelector } from "react-redux";
import { languages } from "../../../../dataFiles/language";
import useFetchCountries from "../../../custom-hook/useFetchCountries";
import DropdownMenu from "../../../../../common/components/dropdown";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import SemButton from "../../../../../common/components/Button";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import WebsiteType from "../rank-tracker/WebsiteType";
import { AITrackerStore } from "../../../../../store/AITrackerStore";
import { ZustandStore } from "../../../../../store/zustand-store";

const AddWebsite = () => {
  const { t } = useTranslation();
  const pathname_loc = useLocation();
  useFetchCountries();
  const [projectName, setProjectName] = useState("");
  const [website, setWebsite] = useState({
    name: "",
    type: "as Sub-domain",
    show: false,
    url: "",
    domain: "",
    sub_domain: "",
    sel_type: "domain",
  });
  const [errors, setErrors] = useState({
    websiteURL: "",
    projectName: "",
    typeSpecific: "",
    errorMsg: "",
  });
  const [language, setLanguage] = useState({
    language_name: "English",
    language_code: "en",
  });
  const [selectedLocations, setSelectedLocations] = useState([]);

  const { customer_plan } = ZustandStore((state) => state);
  const userallprojectname = useSelector((state) => state.userallAIprojectname);
  const { addProject, setMultipleFields, country_Data } = AITrackerStore(
    (state) => state
  );

  const loading = addProject.project_usage.loading;
  const error = addProject.project_usage.error;
  const accUsage = addProject.project_usage.limits;
  const userProjectlimit = customer_plan?.aio_project;

  const MAX_PROJECTS = userProjectlimit || 0;
  const CUR_PROJECTS = accUsage?.total_ai_project || 0;
  const MAX_URLS = customer_plan?.exact_url || 0;
  const canTrackURL =
    loading || error
      ? false
      : (accUsage?.total_ai_project || 0) < MAX_PROJECTS &&
        (accUsage?.total_url || 0) < MAX_URLS;

  // Validation function
  const validate = () => {
    const newErrors = { websiteURL: "", projectName: "", typeSpecific: "" };
    const websiteURL = website.name.trim();

    if (!projectName.trim())
      newErrors.projectName = "Project Name is required!";
    if (!websiteURL) newErrors.websiteURL = "Website URL is required!";
    else if (getUrlType(websiteURL) === "Invalid")
      newErrors.websiteURL = "Invalid domain, subdomain, or URL format!";

    // Type-specific field validation
    if (
      (website.sel_type === "sub_domain" &&
        !website.sub_domain.trim() &&
        website.show) ||
      (website.sel_type === "domain" &&
        !website.domain.trim() &&
        website.show) ||
      (website.sel_type === "URL" && !website.url.trim() && website.show)
    ) {
      newErrors.typeSpecific = `${website.sel_type.replace(
        "_",
        " "
      )} is required.`;
    }

    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };

  useEffect(() => {
    if (addProject.complete_steps?.length > 0) {
      setWebsite({
        name: addProject.domainUrl,
        type: "as Root-domain",
        show: false,
        url: "",
        domain: "",
        sub_domain: "",
        sel_type: "domain",
      });
      setLanguage(addProject.language);
      setSelectedLocations(addProject.location);
      setProjectName(addProject.project_name);
    }
  }, [addProject.complete_steps]);

  // Debounced validation
  useEffect(() => {
    const websiteURL = website.name.trim();
    const isValid = getUrlType(websiteURL) === "Invalid";
    const handler = setTimeout(() => {
      if (websiteURL && !isValid) {
        const processedURL = website.name.includes("://")
          ? website.name
          : `https://${website.name}`;
        const { subdomain, domain } = getDomainParts(processedURL);
        setWebsite((pre) => ({
          ...pre,
          show: true,
          url: processedURL,
          domain: domain,
          sub_domain: subdomain,
          sel_type: subdomain ? "sub_domain" : "domain",
        }));
      } else {
        setWebsite((pre) => ({
          ...pre,
          show: false,
          url: "",
          domain: "",
          sub_domain: "",
          sel_type: "domain",
        }));
      }
    }, 500); // 500ms debounce delay

    return () => clearTimeout(handler); // Cleanup timeout
  }, [website.name]);

  const langData = useMemo(() => {
    const lang = languages.map((lang) => lang.language);
    return {
      value: language.language_name,
      placeholder: "Select Language",
      disabled: false,
      data: lang,
      height: "38px",
      loading: false,
    };
  }, [languages, language.language_name]);

  const locData = useMemo(() => {
    const locationTypeOrder = [
      "Country",
      "State",
      "City",
      "Postal Code",
      "County",
      "District",
      "Neighborhood",
      "Municipality",
    ];
    const sortedLocations = country_Data?.data?.sort((a, b) => {
      const orderA = locationTypeOrder.indexOf(a.location_type);
      const orderB = locationTypeOrder.indexOf(b.location_type);
      return orderA - orderB;
    });
    const loc = sortedLocations?.map((loc) => {
      return {
        location_name: loc.location_name,
        location_type: loc.location_type,
      };
    });
    return {
      value: t("add_project.location_value"),
      placeholder: "Select Location",
      disabled: country_Data.loading || false,
      data: loc,
      height: "38px",
      type: "location",
      loading: country_Data.loading,
    };
  }, [country_Data]);

  const handleAddLanguage = useCallback(
    (value) => {
      const newLanguage = languages.find((lang) => lang.language === value);
      if (!newLanguage) return;
      setLanguage({
        language_name: newLanguage.language,
        language_code: newLanguage.languagecode,
      });
      setSelectedLocations([]);
    },
    [languages]
  );

  const handleAddLocation = useCallback(
    (value, type = "add") => {
      const newLocation = country_Data?.data?.find(
        (location) => location.location_name === value
      );
      if (!newLocation) return;
      setSelectedLocations((prev) => {
        const isPresent = prev.some(
          (item) => item.location_name === newLocation.location_name
        );
        if (isPresent && type === "add") return prev;
        if (isPresent) {
          // Remove it
          return prev.filter(
            (item) => item.location_name !== newLocation.location_name
          );
        } else {
          // Add it
          return [
            ...prev,
            {
              location_name: newLocation.location_name,
              location_code: newLocation?.location_code
                ? newLocation.location_code
                : newLocation.location_code_parent || "",
            },
          ];
        }
      });
    },
    [country_Data?.data]
  );

  const handleSave = () => {
    if (!validate()) return; // Stop submission if errors exist
    const hasReachedLimit = CUR_PROJECTS >= MAX_PROJECTS;

    const projectUrl =
      website.sel_type === "sub_domain" &&
      pathname_loc.pathname != "/ai-tracker"
        ? `${website.sub_domain}.${website.domain}`
        : website.sel_type === "URL"
        ? website.url
        : website.domain;

    const curr_project = `${projectUrl}, ${language.language_name}`;

    const setProjectError = (errorMsg) => {
      setErrors({
        websiteURL: "",
        businessName: "",
        projectName: "",
        typeSpecific: "",
        errorMsg,
      });
    };

    if (hasReachedLimit || selectedLocations?.length === 0) {
      const errorMsg = hasReachedLimit
        ? "You’ve reached your project limit. Upgrade your plan to add more."
        : "Please select at least one location.";
      setProjectError(errorMsg);
      return;
    }

    if (
      Array.isArray(userallprojectname) &&
      userallprojectname.includes(curr_project)
    ) {
      setProjectError(
        "This project already exists for this language. Please change the project language."
      );
      return;
    }

    setMultipleFields({
      type: "Add-Project",
      step: 2,
      domainUrl: projectUrl,
      project_name: projectName,
      language: language,
      location: selectedLocations,
      complete_steps: ["1"],
    });
  };

  return (
    <Box style={{ padding: "0 3px" }}>
      <Flex direction="column" mt={2}>
        <Text tag="label" htmlFor="project-name" size={200}>
          {t("add_project.project_name")}
        </Text>
        <Input
          size="l"
          state={errors.projectName ? "invalid" : "normal"}
          className="sem-input"
        >
          <Input.Value
            placeholder={t("add_project.project_name")}
            autoComplete="off"
            id="project-name"
            value={projectName}
            onChange={(e) => setProjectName(e)}
          />
        </Input>
        {errors.projectName && (
          <Text color="red-500">{errors.projectName}</Text>
        )}
      </Flex>
      <Flex direction="column" my={2}>
        <Text tag="label" htmlFor="websiteURL" size={200}>
          {t("add_project.website_url")}
        </Text>

        <Input
          size="l"
          state={errors.websiteURL ? "invalid" : "normal"}
          className="sem-input"
        >
          <Input.Value
            placeholder={t("add_project.website_url")}
            autoComplete="off"
            id="websiteURL"
            value={website.name}
            onChange={(e) =>
              setWebsite((pre) => ({
                ...pre,
                name: e,
              }))
            }
          />
        </Input>
        {errors.websiteURL && <Text color="red-500">{errors.websiteURL}</Text>}
      </Flex>
      {website.show && (
        <WebsiteType
          {...{
            website,
            setWebsite,
            canTrackURL,
          }}
        />
      )}
      {errors.typeSpecific && (
        <Text color="red-500">{errors.typeSpecific}</Text>
      )}

      <Flex direction="column" mt={2}>
        <Text tag="label" htmlFor="project-name" size={200}>
          {t("add_project.language")}
        </Text>
        <DropdownMenu
          {...{
            menuData: langData,
            handleSelect: handleAddLanguage,
          }}
        />
      </Flex>
      <Flex direction="column" mt={2}>
        <Text tag="label" htmlFor="project-name" size={200}>
          {t("add_project.location")}
        </Text>
        <DropdownMenu
          {...{
            menuData: locData,
            handleSelect: handleAddLocation,
          }}
        />
        <Stack direction="row" spacing={1} my={1}>
          {selectedLocations?.map((location) => (
            <Chip
              key={location.location_name}
              label={location.location_name}
              size="small"
              className="heading-xs"
              onDelete={() =>
                handleAddLocation(location.location_name, "delete")
              }
              sx={{
                height: "20px",
                backgroundColor: "#ECEDF0",
                "& .MuiChip-label": {
                  fontWeight: "500",
                  fontFamily: "Work Sans",
                  color: `#6C6E79 !important`,
                },
              }}
            />
          ))}
        </Stack>
        {errors.errorMsg && <Text color="red-500">{errors.errorMsg}</Text>}
      </Flex>
      <Flex mt={4} flexWrap gap={3}>
        <SemButton
          size="m"
          ml={"auto"}
          name={t("common_buttons.next")}
          onClick={handleSave}
        />
      </Flex>
    </Box>
  );
};

export default AddWebsite;
